import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Download,
  Edit3,
  Eye,
  FileText,
  RefreshCw,
  Save,
  Send,
  X
} from 'lucide-react';
import api from '../../services/api';

const defaultManual = {
  summary: '',
  highlights: '',
  next_actions: '',
  private_note: ''
};

const currency = (value) => `${Number(value || 0).toLocaleString()}원`;

const statusLabel = {
  draft: '작성중',
  generated: '작성완료',
  sent: '전송됨',
  viewed: '확인됨'
};

const statusClass = {
  draft: 'bg-gray-100 text-gray-800',
  generated: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-green-100 text-green-800',
  viewed: 'bg-blue-100 text-blue-800'
};

const getManual = (report) => report?.issue_data?.manual || defaultManual;

const normalizeApiList = (response, key) => {
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response?.[key])) {
    return response[key];
  }
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  return [];
};

const MonthlyReport = () => {
  const queryClient = useQueryClient();
  const [selectedOwner, setSelectedOwner] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedReport, setSelectedReport] = useState(null);
  const [editingReport, setEditingReport] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    summary: '',
    highlights: '',
    next_actions: '',
    private_note: ''
  });

  const { data: propertyOwners = [], isLoading: loadingOwners } = useQuery(
    'rmc-property-owners-for-monthly-report',
    async () => {
      const response = await api.get('/rmc/property-owners');
      return normalizeApiList(response, 'property_owners');
    }
  );

  const { data: buildingsResponse = {}, isLoading: loadingBuildings } = useQuery(
    ['rmc-buildings-for-monthly-report', selectedOwner],
    async () => api.get('/rmc/buildings/', {
      params: { property_owner_id: selectedOwner, per_page: 100 }
    }),
    { enabled: Boolean(selectedOwner) }
  );
  const buildings = useMemo(
    () => normalizeApiList(buildingsResponse, 'buildings'),
    [buildingsResponse]
  );

  useEffect(() => {
    if (selectedOwner || propertyOwners.length === 0) {
      return;
    }

    setSelectedOwner(String(propertyOwners[0].id));
  }, [propertyOwners, selectedOwner]);

  useEffect(() => {
    if (buildings.length === 0) {
      return;
    }

    const hasSelectedBuilding = buildings.some(
      (building) => String(building.id) === String(selectedBuilding)
    );
    if (!hasSelectedBuilding) {
      setSelectedBuilding(String(buildings[0].id));
    }
  }, [buildings, selectedBuilding]);

  const { data: reports = [], isLoading: isLoadingReports } = useQuery(
    ['monthly-reports', selectedOwner, selectedBuilding, selectedMonth],
    async () => {
      const params = {};
      if (selectedOwner) params.property_owner_id = selectedOwner;
      if (selectedBuilding) params.building_id = selectedBuilding;
      if (selectedMonth) params.report_month = selectedMonth;
      return api.get('/rmc/monthly-reports', { params });
    }
  );

  const selectedOwnerName = useMemo(() => {
    const owner = propertyOwners.find((item) => String(item.id) === String(selectedOwner));
    return owner?.name || '';
  }, [propertyOwners, selectedOwner]);
  const selectedBuildingName = useMemo(() => {
    const building = buildings.find((item) => String(item.id) === String(selectedBuilding));
    return building?.name || '';
  }, [buildings, selectedBuilding]);

  const generateReportMutation = useMutation(
    async () => api.post('/rmc/monthly-reports/generate', {
      property_owner_id: selectedOwner,
      building_id: selectedBuilding,
      report_month: selectedMonth
    }),
    {
      onSuccess: (report) => {
        queryClient.invalidateQueries('monthly-reports');
        openEditor(report);
      },
      onError: (error) => {
        alert(`보고서 생성 실패: ${error.response?.data?.error || error.message}`);
      }
    }
  );

  const updateReportMutation = useMutation(
    async ({ reportId, payload }) => api.patch(`/rmc/monthly-reports/${reportId}`, payload),
    {
      onSuccess: (report) => {
        queryClient.invalidateQueries('monthly-reports');
        setEditingReport(null);
        setSelectedReport(report);
        alert('보고서가 저장되었습니다.');
      },
      onError: (error) => {
        alert(`보고서 저장 실패: ${error.response?.data?.error || error.message}`);
      }
    }
  );

  const sendReportMutation = useMutation(
    async (reportId) => api.post(`/rmc/monthly-reports/${reportId}/send`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('monthly-reports');
        alert('보고서가 이메일로 전송되었습니다.');
      },
      onError: (error) => {
        alert(`전송 실패: ${error.response?.data?.error || error.message}`);
      }
    }
  );

  const handleGenerate = () => {
    if (!selectedOwner) {
      alert('임대사업자를 선택해주세요.');
      return;
    }
    if (!selectedBuilding) {
      alert('건물을 선택해주세요.');
      return;
    }
    const message = `${selectedOwnerName || '선택한 임대사업자'}의 ${selectedBuildingName} ${selectedMonth} 월간 보고서를 생성/갱신할까요?`;
    if (window.confirm(message)) {
      generateReportMutation.mutate();
    }
  };

  const openEditor = (report) => {
    const manual = getManual(report);
    setEditingReport(report);
    setEditForm({
      title: report.title || `${report.report_month} 월간 운영 보고서`,
      summary: manual.summary || '',
      highlights: manual.highlights || '',
      next_actions: manual.next_actions || '',
      private_note: manual.private_note || ''
    });
  };

  const handleSave = () => {
    if (!editingReport) return;
    updateReportMutation.mutate({
      reportId: editingReport.id,
      payload: editForm
    });
  };

  const handleDownload = async (report) => {
    try {
      const response = await api.get(`/rmc/monthly-reports/${report.id}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/html;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `월간보고서_${report.report_month}_${report.building_name || report.building_id}.html`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(`다운로드 실패: ${error.response?.data?.error || error.message}`);
    }
  };

  const renderPreview = (report) => {
    const financial = report.financial_data || {};
    const occupancy = report.occupancy_data || {};
    const maintenance = report.maintenance_data || {};
    const issue = report.issue_data || {};
    const manual = getManual(report);
    const financialDetails = financial.details || {};
    const vacancyBasicCosts = financialDetails.vacancy_basic_costs || [];

    return (
      <div className="space-y-6">
        <section>
          <h3 className="mb-3 border-b pb-2 text-base font-semibold">월간 지표</h3>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div className="rounded-lg border p-3">
              <p className="text-xs text-gray-500">총 수입</p>
              <p className="mt-1 text-lg font-bold text-blue-600">{currency(financial.total_income)}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-gray-500">총 지출</p>
              <p className="mt-1 text-lg font-bold text-red-600">{currency(financial.total_expense)}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-gray-500">순수익</p>
              <p className="mt-1 text-lg font-bold text-green-600">{currency(financial.net_income)}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-gray-500">입주율</p>
              <p className="mt-1 text-lg font-bold">{occupancy.occupancy_rate || 0}%</p>
            </div>
          </div>
        </section>

        <section>
          <h3 className="mb-3 border-b pb-2 text-base font-semibold">공실/입주 현황</h3>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <div className="rounded-lg border p-3 text-center">
              <p className="text-xs text-gray-500">전체세대수</p>
              <p className="mt-1 text-lg font-bold">{occupancy.total_units || 0}</p>
            </div>
            <div className="rounded-lg border p-3 text-center">
              <p className="text-xs text-gray-500">입주중</p>
              <p className="mt-1 text-lg font-bold text-blue-600">{occupancy.occupied_units || 0}</p>
            </div>
            <div className="rounded-lg border p-3 text-center">
              <p className="text-xs text-gray-500">당월증감</p>
              <p className={`mt-1 text-lg font-bold ${(occupancy.monthly_change || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                {(occupancy.monthly_change || 0) > 0 ? '+' : ''}{occupancy.monthly_change || 0}
              </p>
            </div>
            <div className="rounded-lg border p-3 text-center">
              <p className="text-xs text-gray-500">입주율</p>
              <p className="mt-1 text-lg font-bold">{occupancy.occupancy_rate || 0}%</p>
            </div>
            <div className="rounded-lg border p-3 text-center">
              <p className="text-xs text-gray-500">익월계약만료</p>
              <p className="mt-1 text-lg font-bold text-amber-600">{occupancy.next_month_expiring || 0}</p>
            </div>
            <div className="rounded-lg border p-3 text-center">
              <p className="text-xs text-gray-500">익월입주예정</p>
              <p className="mt-1 text-lg font-bold text-green-600">{occupancy.next_month_move_in || 0}</p>
            </div>
          </div>
        </section>

        <section>
          <h3 className="mb-3 border-b pb-2 text-base font-semibold">월간 운영요약</h3>
          <div className="space-y-2 rounded-lg bg-gray-50 p-4 text-sm leading-6 text-gray-700">
            <p>{report.report_month} 임대료 청구: <b>{financial.billing_count || 0}건</b> / <b>{currency(financial.billing_amount)}</b></p>
            <p>{report.report_month} 임대료 수납: <b>{financial.collection_count || 0}건</b> / <b>{currency(financial.collection_amount)}</b></p>
            <p>미납: <b>{financial.unpaid_count || 0}건</b> / <b className="text-red-600">{currency(financial.unpaid_amount)}</b></p>
            <p>유지보수: <b>{maintenance.total_requests || 0}건</b> 발생, 처리완료 <b>{maintenance.completed || 0}건</b>, 보류 <b>{maintenance.pending || 0}건</b></p>
            <p>공실 기본비용: 임대사업자 부담 <b>{currency(financialDetails.vacancy_basic_expense)}</b>, 임대관리자 부담 <b>{currency(financialDetails.vacancy_basic_rental_manager_burden)}</b></p>
            <p>입실퇴실점검: 입실 <b>{issue.operations?.move_in_count || 0}건</b>, 퇴실 <b>{issue.operations?.move_out_count || 0}건</b></p>
          </div>
        </section>

        <section>
          <h3 className="mb-3 border-b pb-2 text-base font-semibold">주요 특이사항</h3>
          <p className="whitespace-pre-wrap text-sm leading-6 text-gray-700">
            {manual.highlights || '작성된 특이사항이 없습니다.'}
          </p>
        </section>

        <section>
          <h3 className="mb-3 border-b pb-2 text-base font-semibold">향후 조치 계획</h3>
          <p className="whitespace-pre-wrap text-sm leading-6 text-gray-700">
            {manual.next_actions || '작성된 조치 계획이 없습니다.'}
          </p>
        </section>

        <section>
          <h3 className="mb-3 border-b pb-2 text-base font-semibold">유지보수 현황</h3>
          <div className="space-y-2">
            {maintenance.list?.length > 0 ? maintenance.list.map((item, index) => (
              <div key={`${item.id}-${index}`} className="rounded-lg border p-3 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="font-medium">{item.building_name} {item.unit_number}호</span>
                  <span className="text-gray-500">{item.status}</span>
                </div>
                <p className="mt-1 text-gray-700">{item.issue}</p>
                <p className="mt-1 text-right font-medium">{currency(item.cost)}</p>
              </div>
            )) : (
              <p className="text-sm text-gray-500">유지보수 내역이 없습니다.</p>
            )}
          </div>
        </section>

        <section>
          <h3 className="mb-3 border-b pb-2 text-base font-semibold">공실 기본비용</h3>
          {vacancyBasicCosts.length > 0 ? (
            <div className="space-y-2">
              {vacancyBasicCosts.map((item, index) => (
                <div key={`${item.unit_id}-${index}`} className="rounded-lg border p-3 text-sm">
                  <div className="flex justify-between gap-3">
                    <span className="font-medium">{item.unit_number || '-'}</span>
                    <span className={item.payer === 'rental_manager' ? 'text-indigo-600' : 'text-gray-600'}>
                      {item.payer === 'rental_manager' ? '임대관리자 부담' : '임대사업자 부담'}
                    </span>
                  </div>
                  <div className="mt-1 flex justify-between text-gray-600">
                    <span>공실 {item.vacancy_days || 0}/{item.month_days || 0}일</span>
                    <span className="font-medium text-gray-900">{currency(item.amount)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">공실 기본비용이 없습니다.</p>
          )}
        </section>

        <section>
          <h3 className="mb-3 border-b pb-2 text-base font-semibold">미납 현황</h3>
          <p className="mb-2 text-sm">
            총 미납 건수 <b>{issue.overdue_count || 0}</b>건 · 총 미납액{' '}
            <b className="text-red-600">{currency(issue.overdue_total_amount)}</b>
          </p>
          {issue.list?.length > 0 ? (
            <div className="space-y-2">
              {issue.list.map((item, index) => (
                <div key={`${item.tenant}-${index}`} className="flex justify-between rounded-lg border p-3 text-sm">
                  <span>{item.tenant || '-'}</span>
                  <span className="font-medium text-red-600">{currency(item.amount)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">주요 미납 이슈가 없습니다.</p>
          )}
        </section>
      </div>
    );
  };

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
          <FileText className="h-6 w-6" />
          월간 운영 보고서
        </h1>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_1fr_180px_auto] md:items-end">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">임대사업자</label>
            <select
              className="w-full rounded-md border border-gray-300 px-3 py-2"
              value={selectedOwner}
              onChange={(e) => {
                setSelectedOwner(e.target.value);
                setSelectedBuilding('');
              }}
              disabled={loadingOwners}
            >
              <option value="">선택</option>
              {propertyOwners.map((owner) => (
                <option key={owner.id} value={owner.id}>{owner.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">건물</label>
            <select
              className="w-full rounded-md border border-gray-300 px-3 py-2"
              value={selectedBuilding}
              onChange={(e) => setSelectedBuilding(e.target.value)}
              disabled={!selectedOwner || loadingBuildings}
            >
              <option value="">선택</option>
              {buildings.map((building) => (
                <option key={building.id} value={building.id}>{building.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">보고월</label>
            <input
              type="month"
              className="w-full rounded-md border border-gray-300 px-3 py-2"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            />
          </div>
          <button
            onClick={handleGenerate}
            disabled={generateReportMutation.isLoading || !selectedOwner || !selectedBuilding}
            className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-gray-400"
          >
            {generateReportMutation.isLoading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            생성/갱신
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="hidden overflow-x-auto md:block">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">보고월</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">임대사업자</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">건물</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">제목</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">상태</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">생성일</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">전송일</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">관리</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoadingReports ? (
                <tr><td colSpan="8" className="px-4 py-6 text-center text-gray-500">로딩 중...</td></tr>
              ) : reports.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-6 text-center text-gray-500">생성된 보고서가 없습니다.</td></tr>
              ) : reports.map((report) => (
                <tr key={report.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{report.report_month}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{report.property_owner_name}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{report.building_name || '기존 통합보고서'}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{report.title}</td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusClass[report.status] || statusClass.draft}`}>
                      {statusLabel[report.status] || report.status}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                    {report.created_at ? new Date(report.created_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                    {report.sent_at ? new Date(report.sent_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm">
                    <div className="inline-flex items-center gap-2">
                      <button onClick={() => setSelectedReport(report)} className="text-blue-600 hover:text-blue-900" title="미리보기">
                        <Eye className="h-5 w-5" />
                      </button>
                      <button onClick={() => openEditor(report)} className="text-gray-700 hover:text-gray-900" title="작성/수정">
                        <Edit3 className="h-5 w-5" />
                      </button>
                      <button onClick={() => handleDownload(report)} className="text-indigo-600 hover:text-indigo-900" title="다운로드">
                        <Download className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('임대사업자 이메일로 보고서를 전송할까요?')) {
                            sendReportMutation.mutate(report.id);
                          }
                        }}
                        className="text-green-600 hover:text-green-900"
                        title="이메일 전송"
                      >
                        <Send className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-3 md:hidden">
          {isLoadingReports ? (
            <div className="rounded-lg border px-4 py-6 text-center text-sm text-gray-500">로딩 중...</div>
          ) : reports.length === 0 ? (
            <div className="rounded-lg border px-4 py-6 text-center text-sm text-gray-500">생성된 보고서가 없습니다.</div>
          ) : reports.map((report) => (
            <div key={report.id} className="rounded-lg border border-gray-200 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm text-gray-500">{report.report_month}</div>
                  <div className="mt-1 font-semibold text-gray-900">{report.title}</div>
                  <div className="mt-1 text-sm text-gray-600">{report.property_owner_name}</div>
                  <div className="mt-1 text-sm font-medium text-gray-700">{report.building_name || '기존 통합보고서'}</div>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-1 text-xs font-semibold ${statusClass[report.status] || statusClass.draft}`}>
                  {statusLabel[report.status] || report.status}
                </span>
              </div>
              <div className="mt-4 grid grid-cols-4 gap-2">
                <button onClick={() => setSelectedReport(report)} className="rounded-md border px-2 py-2 text-xs font-medium text-blue-700">보기</button>
                <button onClick={() => openEditor(report)} className="rounded-md border px-2 py-2 text-xs font-medium text-gray-800">작성</button>
                <button onClick={() => handleDownload(report)} className="rounded-md border px-2 py-2 text-xs font-medium text-indigo-700">저장</button>
                <button
                  onClick={() => {
                    if (window.confirm('임대사업자 이메일로 보고서를 전송할까요?')) {
                      sendReportMutation.mutate(report.id);
                    }
                  }}
                  className="rounded-md border px-2 py-2 text-xs font-medium text-green-700"
                >
                  전송
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white shadow-xl">
            <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white p-4">
              <div>
                <h2 className="text-lg font-bold">{selectedReport.title}</h2>
                <p className="text-sm text-gray-500">{selectedReport.property_owner_name} · {selectedReport.building_name} · {selectedReport.report_month}</p>
              </div>
              <button onClick={() => setSelectedReport(null)} className="rounded-md p-2 text-gray-500 hover:bg-gray-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 sm:p-6">{renderPreview(selectedReport)}</div>
            <div className="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 p-4">
              <button onClick={() => openEditor(selectedReport)} className="inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm hover:bg-white">
                <Edit3 className="h-4 w-4" /> 작성/수정
              </button>
              <button onClick={() => handleDownload(selectedReport)} className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">
                <Download className="h-4 w-4" /> 다운로드
              </button>
            </div>
          </div>
        </div>
      )}

      {editingReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-xl">
            <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white p-4">
              <div>
                <h2 className="text-lg font-bold">월간 보고서 작성</h2>
                <p className="text-sm text-gray-500">{editingReport.property_owner_name} · {editingReport.building_name} · {editingReport.report_month}</p>
              </div>
              <button onClick={() => setEditingReport(null)} className="rounded-md p-2 text-gray-500 hover:bg-gray-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 p-4 sm:p-6">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">제목</label>
                <input
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                  value={editForm.title}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">운영 요약</label>
                <textarea
                  rows={4}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                  value={editForm.summary}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, summary: e.target.value }))}
                  placeholder="이번 달 운영 성과, 수입/지출 흐름, 공실 현황을 요약하세요."
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">주요 특이사항</label>
                <textarea
                  rows={4}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                  value={editForm.highlights}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, highlights: e.target.value }))}
                  placeholder="미납, 민원, 유지보수, 공실 대응 등 임대사업자가 알아야 할 내용을 적으세요."
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">향후 조치 계획</label>
                <textarea
                  rows={4}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                  value={editForm.next_actions}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, next_actions: e.target.value }))}
                  placeholder="다음 달 중점 관리 사항과 요청 사항을 적으세요."
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">내부 메모</label>
                <textarea
                  rows={3}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                  value={editForm.private_note}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, private_note: e.target.value }))}
                  placeholder="전송 문서에는 표시하지 않을 내부 참고 메모입니다."
                />
              </div>
            </div>
            <div className="sticky bottom-0 flex justify-end gap-2 border-t border-gray-200 bg-gray-50 p-4">
              <button onClick={() => setEditingReport(null)} className="rounded-md border px-4 py-2 text-sm hover:bg-white">취소</button>
              <button
                onClick={handleSave}
                disabled={updateReportMutation.isLoading}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-gray-400"
              >
                {updateReportMutation.isLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                저장
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyReport;
